import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClassroom } from '../contexts/ClassroomContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Gamepad2, Play, Clock, Trash2, ChevronDown, RotateCcw, BookOpen, MapPin, ArrowRight, GraduationCap, Timer, ListOrdered, Plus, FolderOpen, Library, ChevronRight, FlaskConical, School } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_LABELS = {
  la_course: 'La Course', la_tour: 'La Tour', robot_balayeur: 'Robot Balayeur',
  tri_express: 'Tri Express', batisseur: 'Batisseur', detective: 'Le Detective',
  memoire: 'La Memoire', avalanche: "L'Avalanche", le_chef: 'Le Chef',
  carte_du_canada: 'Carte du Canada', duel_conjugaison: 'Le Duel',
  horloge_folle: "L'Horloge Folle",
};

export function Dashboard() {
  const { user } = useAuth();
  const { activeClassroom } = useClassroom();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_games: 0, this_week: 0, favorite_game: null });
  const [sessions, setSessions] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [samplePlaylists, setSamplePlaylists] = useState([]);
  const [showSamples, setShowSamples] = useState(false);
  const [showNewPlaylist, setShowNewPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [expandedSession, setExpandedSession] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [curriculumBC, setCurriculumBC] = useState(null);
  const [cefrProgress, setCefrProgress] = useState(null);

  useEffect(() => {
    if (user?.demo) return;
    const cid = activeClassroom?.id;
    const q = cid ? `?classroom_id=${cid}` : '';
    axios.get(`${API_URL}/teacher/stats${q}`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/sessions${q ? q + '&' : '?'}limit=10`).then(r => setSessions(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/playlists`).then(r => setPlaylists(r.data)).catch(() => {});
    axios.get(`${API_URL}/templates`).then(r => setSamplePlaylists(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/curriculum-coverage${q}`).then(r => setCurriculum(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/curriculum-coverage-bc${q}`).then(r => setCurriculumBC(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/progress-cefr${q}`).then(r => setCefrProgress(r.data)).catch(() => {});
  }, [user, activeClassroom]);

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    try {
      await axios.post(`${API_URL}/teacher/playlists`, { name: newPlaylistName.trim() });
      const res = await axios.get(`${API_URL}/teacher/playlists`);
      setPlaylists(res.data);
      setNewPlaylistName('');
      setShowNewPlaylist(false);
    } catch { /* ignore */ }
  };

  const deletePlaylist = async (id) => {
    await axios.delete(`${API_URL}/teacher/playlists/${id}`);
    setPlaylists(p => p.filter(x => x.id !== id));
  };

  const replaySession = (s) => {
    const c = s.config || {};
    const params = new URLSearchParams(c);
    navigate(`/play/${s.game}/setup?${params}`);
  };

  const isNew = !user?.demo && stats.total_games === 0 && playlists.length === 0;

  return (
    <div data-testid="dashboard-page" style={{ height: '100%', overflow: 'auto', padding: '2rem 2.5rem', background: '#F8F9FB' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
          {user?.demo ? t('dash.welcomeDemo') : `${t('dash.welcomeBack')}, ${user?.name || 'Teacher'}`}
        </h1>
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B', margin: '0.25rem 0 0' }}>
          {user?.demo ? t('dash.subtitleDemo') : t('dash.subtitleDefault')}
        </p>
      </div>

      {/* Trial countdown — subtle, non-pushy */}
      {user?.access_status === 'trial' && user.trial_days_remaining != null && (
        <div
          data-testid="trial-banner"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 1rem', borderRadius: '0.625rem',
            background: '#F0F4FF', border: '1px solid #E0E7FF',
            marginBottom: '1.25rem',
          }}
        >
          <Timer size={14} color="#4C6EF5" />
          <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#4C6EF5', fontWeight: 600 }}>
            {user.trial_days_remaining} {user.trial_days_remaining !== 1 ? t('dash.days') : t('dash.day')} {t('dash.trialRemaining')}
          </span>
        </div>
      )}

      {/* Classroom context banner */}
      {!user?.demo && activeClassroom && (
        <div
          data-testid="classroom-context-banner"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem',
            padding: '0.625rem 1rem', borderRadius: '0.625rem',
            background: '#F8F9FB',
            border: '1px solid #E2E8F0',
            marginBottom: '1.25rem',
          }}
        >
          {activeClassroom.type === 'sandbox'
            ? <FlaskConical size={15} color="#64748B" />
            : <School size={15} color="#1E2140" />
          }
          <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B' }}>
            {activeClassroom.type === 'sandbox' ? (
              <>
                <strong style={{ color: '#1A1D20' }}>{activeClassroom.name}</strong>
                {' '}&mdash; {t('dash.sandboxDesc')}
              </>
            ) : (
              <>
                <strong style={{ color: '#1A1D20' }}>{activeClassroom.name}</strong>
                {' '}&mdash; {t('dash.classroomDesc')}
              </>
            )}
          </span>
        </div>
      )}

      {/* Quick action */}
      <button
        onClick={() => navigate('/browse')}
        data-testid="dashboard-start-game"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.875rem 2rem', borderRadius: '0.875rem',
          background: '#1E2140', color: 'white', border: 'none',
          fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 600,
          cursor: 'pointer', marginBottom: '2rem', transition: 'transform 0.15s, box-shadow 0.15s',
          boxShadow: '0 2px 8px rgba(30,33,64,0.2)',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(30,33,64,0.3)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,33,64,0.2)'; }}
      >
        <Play size={20} fill="white" />
        {t('dash.startNewGame')}
      </button>

      {/* Stats — simple summary line */}
      {!user?.demo && !isNew && (
        <div data-testid="dashboard-stats-summary" style={{
          display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem',
          padding: '1rem 1.5rem', background: 'white', borderRadius: '0.875rem',
          border: '1px solid #E2E8F0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Gamepad2 size={16} color="#4C6EF5" />
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#64748B' }}>
              <strong style={{ color: '#1A1D20', fontFamily: 'Fredoka, sans-serif', fontSize: '1.1rem' }}>{stats.total_games}</strong> {t('dash.gamesPlayed')}
            </span>
          </div>
          {stats.favorite_game && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#64748B' }}>
                {t('dash.mostPlayed')}: <strong style={{ color: '#1A1D20' }}>{GAME_LABELS[stats.favorite_game] || stats.favorite_game}</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Progress Summary */}
      {!user?.demo && (cefrProgress || curriculum || curriculumBC) && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 600, color: '#1A1D20', margin: 0 }}>
              {t('dash.progress')}
            </h3>
            <button
              onClick={() => navigate('/progress')}
              data-testid="progress-view-full"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                color: '#4C6EF5',
              }}
            >
              {t('dash.viewFullReport')} <ArrowRight size={14} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
            {/* CEFR card — universal, shown first */}
            {cefrProgress && cefrProgress.levels_total > 0 && (
              <CEFRSummaryCard
                data={cefrProgress}
                onClick={() => navigate('/progress')}
              />
            )}
            {curriculum && curriculum.total > 0 && (
              <CurriculumDotCard
                label="Ontario"
                icon={BookOpen}
                practiced={curriculum.practiced || 0}
                total={curriculum.total}
                sessionCount={curriculum.session_count || 0}
                gameCount={curriculum.unique_games || 0}
                groups={curriculum.by_strand}
                colorMap={STRAND_COLORS}
                onClick={() => navigate('/progress')}
              />
            )}
            {curriculumBC && curriculumBC.total > 0 && (
              <CurriculumDotCard
                label="British Columbia"
                icon={MapPin}
                practiced={curriculumBC.practiced || 0}
                total={curriculumBC.total}
                sessionCount={curriculumBC.session_count || 0}
                gameCount={curriculumBC.unique_games || 0}
                groups={curriculumBC.by_grade}
                colorMap={GRADE_COLORS}
                onClick={() => navigate('/progress')}
              />
            )}
          </div>
        </div>
      )}

      {/* Playlists */}
      {!user?.demo && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 600, color: '#1A1D20', margin: 0 }}>
              {t('dash.playlists')}
            </h3>
            <button
              data-testid="create-playlist-btn"
              onClick={() => setShowNewPlaylist(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                color: '#4C6EF5',
              }}
            >
              <Plus size={14} /> {t('dash.newPlaylist')}
            </button>
          </div>

          {/* New Playlist Inline Form */}
          {showNewPlaylist && (
            <div data-testid="new-playlist-form" style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              marginBottom: '0.75rem', padding: '0.75rem 1rem',
              background: 'white', borderRadius: '0.75rem', border: '1px solid #4C6EF5',
            }}>
              <input
                data-testid="new-playlist-name-input"
                value={newPlaylistName}
                onChange={e => setNewPlaylistName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createPlaylist()}
                placeholder="e.g., Summer After-School Program"
                autoFocus
                style={{
                  flex: 1, fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem',
                  border: 'none', outline: 'none', color: '#1A1D20', background: 'transparent',
                }}
              />
              <button
                data-testid="new-playlist-save-btn"
                onClick={createPlaylist}
                style={{
                  padding: '0.375rem 1rem', borderRadius: '0.5rem',
                  background: '#1E2140', color: 'white', border: 'none',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('dash.create')}
              </button>
              <button
                data-testid="new-playlist-cancel-btn"
                onClick={() => { setShowNewPlaylist(false); setNewPlaylistName(''); }}
                style={{
                  padding: '0.375rem 0.75rem', borderRadius: '0.5rem',
                  background: 'transparent', color: '#94A3B8', border: '1px solid #E2E8F0',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('dash.cancel')}
              </button>
            </div>
          )}

          {playlists.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
              {playlists.map(pl => (
                <div
                  key={pl.id}
                  data-testid={`playlist-card-${pl.id}`}
                  style={{
                    background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
                    padding: '1rem 1.25rem', cursor: 'pointer', transition: 'all 0.2s',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#1E2140'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
                  onClick={() => navigate(`/playlists/${pl.id}`)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '0.625rem',
                      background: '#F0F4FF', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>
                      <ListOrdered size={18} color="#4C6EF5" />
                    </div>
                    <div>
                      <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#1A1D20', fontSize: '0.925rem' }}>
                        {pl.name}
                      </div>
                      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.15rem' }}>
                        {pl.item_count} {pl.item_count !== 1 ? t('dash.items') : t('dash.item')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePlaylist(pl.id); }}
                    data-testid={`delete-playlist-${pl.id}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '4px' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : !showNewPlaylist ? (
            <div style={{
              textAlign: 'center', padding: '2rem', background: 'white',
              borderRadius: '0.875rem', border: '1px solid #E2E8F0',
            }}>
              <FolderOpen size={32} color="#CBD5E1" style={{ marginBottom: '0.5rem' }} />
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#94A3B8', fontSize: '0.85rem', margin: 0 }}>
                {t('dash.emptyPlaylist')}
              </p>
            </div>
          ) : null}

          {/* Sample Playlists folder */}
          {samplePlaylists.length > 0 && (
            <div style={{ marginTop: playlists.length > 0 || showNewPlaylist ? '1rem' : 0 }}>
              <button
                data-testid="sample-playlists-folder"
                onClick={() => setShowSamples(s => !s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
                  padding: '0.75rem 1rem', borderRadius: '0.75rem',
                  background: 'white', border: '1px solid #E2E8F0',
                  cursor: 'pointer', transition: 'all 0.15s',
                  textAlign: 'left',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#94A3B8'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
              >
                <div style={{
                  width: 38, height: 38, borderRadius: '0.625rem',
                  background: '#F8F0FF', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0,
                }}>
                  <Library size={18} color="#7950F2" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#1A1D20', fontSize: '0.925rem' }}>
                    {t('dash.samplePlaylists')}
                  </div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8' }}>
                    {samplePlaylists.length} {t('dash.preMadeSequences')}
                  </div>
                </div>
                <ChevronDown
                  size={16} color="#94A3B8"
                  style={{ transform: showSamples ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0 }}
                />
              </button>

              {showSamples && (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '0.75rem', marginTop: '0.75rem',
                }}>
                  {samplePlaylists.map(sp => (
                    <div
                      key={sp.id}
                      data-testid={`sample-card-${sp.id}`}
                      onClick={() => navigate(`/samples/${sp.id}`)}
                      style={{
                        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
                        padding: '1rem 1.25rem', cursor: 'pointer', transition: 'all 0.2s',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#7950F2'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(121,80,242,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: '0.625rem',
                          background: '#F0F4FF', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0,
                        }}>
                          <ListOrdered size={18} color="#4C6EF5" />
                        </div>
                        <div>
                          <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#1A1D20', fontSize: '0.925rem' }}>
                            {sp.title}
                          </div>
                          <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.15rem' }}>
                            {sp.total_sessions} sessions &middot; {sp.subtitle}
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={16} color="#CBD5E1" style={{ flexShrink: 0, marginTop: '0.25rem' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent sessions — expandable */}
      {!user?.demo && sessions.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.75rem' }}>
            {t('dash.recentSessions')}
          </h3>
          <div style={{ background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            {sessions.map((s, i) => {
              const isExpanded = expandedSession === i;
              const gameName = s.game_name || GAME_LABELS[s.game] || s.game;
              const scoreStr = s.score?.team1 !== undefined ? `${s.score.team1}\u2013${s.score.team2}` : '';
              const hasConfig = s.config && Object.keys(s.config).length > 0;

              return (
                <div key={i} data-testid={`session-row-${i}`}>
                  {/* Row header — clickable */}
                  <div
                    onClick={() => setExpandedSession(isExpanded ? null : i)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.875rem 1.25rem',
                      borderBottom: (i < sessions.length - 1 || isExpanded) ? '1px solid #F1F5F9' : 'none',
                      cursor: 'pointer', transition: 'background 0.1s',
                      background: isExpanded ? '#FAFBFC' : 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '0.5rem', background: '#F1F5F9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Gamepad2 size={16} color="#64748B" />
                      </div>
                      <div>
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, color: '#1A1D20', fontSize: '0.9rem' }}>
                          {gameName}
                        </div>
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8' }}>
                          {s.difficulty}{s.total_questions ? ` \u2022 ${s.total_questions}q` : ''}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ textAlign: 'right' }}>
                        {scoreStr && (
                          <div style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: '#1A1D20', fontSize: '0.9rem' }}>
                            {scoreStr}
                          </div>
                        )}
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'flex-end' }}>
                          <Clock size={11} />
                          {s.played_at ? new Date(s.played_at).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <ChevronDown size={16} color="#94A3B8" style={{
                        transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      }} />
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{
                      padding: '0.875rem 1.25rem 1rem', background: '#FAFBFC',
                      borderBottom: i < sessions.length - 1 ? '1px solid #F1F5F9' : 'none',
                    }}>
                      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
                        {s.mode && (
                          <DetailChip label="Mode" value={s.mode} />
                        )}
                        {s.difficulty && (
                          <DetailChip label="Difficulty" value={s.difficulty} />
                        )}
                        {s.total_questions > 0 && (
                          <DetailChip label="Questions" value={s.total_questions} />
                        )}
                        {s.duration_seconds > 0 && (
                          <DetailChip label="Duration" value={`${Math.floor(s.duration_seconds / 60)}m ${s.duration_seconds % 60}s`} />
                        )}
                        {scoreStr && (
                          <DetailChip label="Score" value={scoreStr} />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.625rem' }}>
                        {hasConfig && (
                          <button
                            data-testid={`session-replay-${i}`}
                            onClick={(e) => { e.stopPropagation(); replaySession(s); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.4rem',
                              padding: '0.5rem 1rem', borderRadius: '0.5rem',
                              background: '#1E2140', color: 'white', border: 'none',
                              fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <RotateCcw size={14} /> {t('dash.playAgain')}
                          </button>
                        )}
                        <button
                          data-testid={`session-browse-${i}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const c = s.config || {};
                            if (c.mode === 'level' && c.level) {
                              navigate(`/level/${encodeURIComponent(c.level)}`);
                            } else if (c.mode === 'topic' && c.topic) {
                              navigate(`/topic/${c.topic}`);
                            } else if (c.mode === 'school' && c.grade) {
                              navigate(`/school/${c.grade}`);
                            } else {
                              navigate('/browse');
                            }
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                            padding: '0.5rem 1rem', borderRadius: '0.5rem',
                            background: 'transparent', color: '#64748B',
                            border: '1.5px solid #E2E8F0',
                            fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {t('dash.browseContent')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {isNew && (
        <div data-testid="dashboard-empty-state" style={{
          textAlign: 'center', padding: '3rem 2rem', background: 'white',
          borderRadius: '1rem', border: '1px solid #E2E8F0',
        }}>
          <Gamepad2 size={48} color="#94A3B8" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.2rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
            {t('dash.noGamesYet')}
          </h3>
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#64748B', marginBottom: '1.5rem', fontSize: '0.925rem' }}>
            {t('dash.noGamesDesc')}
          </p>
          <button
            onClick={() => navigate('/browse')}
            data-testid="dashboard-browse-games"
            style={{
              padding: '0.75rem 2rem', borderRadius: '0.75rem',
              background: '#1E2140', color: 'white', border: 'none',
              fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            {t('dash.browseGames')}
          </button>
        </div>
      )}
    </div>
  );
}

function DetailChip({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#1A1D20' }}>
        {value}
      </div>
    </div>
  );
}

const STRAND_COLORS = {
  Listening: '#4C6EF5',
  Speaking: '#E03131',
  Reading: '#2F9E44',
  Writing: '#F59F00',
};

const GRADE_COLORS = {
  'Grade 5': '#7C3AED',
  'Grade 6': '#0891B2',
  'Grade 7': '#C2410C',
};

const CEFR_COLORS = {
  'PRE-A1': '#2F9E44',
  'A1': '#4C6EF5',
  'A1-A2': '#E8590C',
  'A2': '#E03131',
};

function CEFRSummaryCard({ data, onClick }) {
  const levels = data.by_level || {};
  const { t } = useLanguage();
  return (
    <div
      data-testid="cefr-summary-card"
      onClick={onClick}
      style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1rem 1.25rem', cursor: 'pointer', transition: 'all 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#4C6EF5'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
        <GraduationCap size={16} color="#1E2140" />
        <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: '#1A1D20' }}>
          {t('dash.cefrLevels')}
        </span>
      </div>
      <div style={{ marginBottom: '0.625rem' }}>
        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#1A1D20' }}>
          {t('dash.activeIn')}{' '}
          <strong style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.1rem' }}>{data.levels_touched}</strong>
          {' '}{t('dash.of')} {data.levels_total} {t('dash.levels')}
          {data.topics_touched > 0 && (
            <span style={{ color: '#64748B' }}> &middot; {data.topics_touched} {t('dash.topics')}</span>
          )}
        </span>
      </div>
      {/* Level bars */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0.625rem' }}>
        {Object.entries(levels).map(([cefr, lv]) => (
          <div
            key={cefr}
            title={`${lv.label}${lv.touched ? ' (active)' : ''}`}
            style={{
              flex: 1, height: 10, borderRadius: 3,
              background: lv.touched ? (CEFR_COLORS[cefr] || '#94A3B8') : '#F1F5F9',
              border: lv.touched ? 'none' : '1px solid #E2E8F0',
            }}
          />
        ))}
      </div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', color: '#94A3B8' }}>
        {t('dash.across')} {data.session_count} {data.session_count !== 1 ? t('dash.sessions') : t('dash.session')}
        {data.unique_games > 0 && <> &middot; {data.unique_games} {data.unique_games !== 1 ? t('dash.games') : t('dash.game')}</>}
      </div>
      <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        {Object.entries(levels).map(([cefr, lv]) => (
          <div key={cefr} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: CEFR_COLORS[cefr] || '#94A3B8' }} />
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', fontWeight: 600, color: '#1A1D20' }}>{cefr}</span>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', color: '#94A3B8' }}>
              {lv.topics_touched}/{lv.topics_total}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


function CurriculumDotCard({ label, icon: Icon, practiced, total, sessionCount, gameCount, groups, colorMap, onClick }) {
  const { t } = useLanguage();
  // Build flat array of dots from groups
  const dots = [];
  if (groups) {
    Object.entries(groups).forEach(([name, info]) => {
      const items = info.expectations || info.content_areas || [];
      items.forEach(exp => {
        dots.push({
          code: exp.code,
          practiced: exp.practiced,
          color: colorMap[name] || '#94A3B8',
          group: name,
        });
      });
    });
  }

  return (
    <div
      data-testid={`curriculum-summary-${label.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
      style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1rem 1.25rem', cursor: 'pointer', transition: 'all 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#4C6EF5'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
        <Icon size={16} color="#1E2140" />
        <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: '#1A1D20' }}>
          {label}
        </span>
      </div>

      {/* Headline: areas touched — no percentage */}
      <div style={{ marginBottom: '0.625rem' }}>
        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#1A1D20' }}>
          {t('dash.yourSessionsInclude')}{' '}
          <strong style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.1rem' }}>{practiced}</strong>
          {' '}{t('dash.of')} {total} {t('dash.curriculumAreas')}
        </span>
      </div>

      {/* Dot grid */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '0.625rem' }}>
        {dots.map((dot, i) => (
          <div
            key={i}
            title={dot.code}
            style={{
              width: 14, height: 14, borderRadius: 3,
              background: dot.practiced ? dot.color : '#F1F5F9',
              border: dot.practiced ? 'none' : '1px solid #E2E8F0',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>

      {/* Context line */}
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', color: '#94A3B8' }}>
        {t('dash.across')} {sessionCount} {sessionCount !== 1 ? t('dash.sessions') : t('dash.session')}
        {gameCount > 0 && <> &middot; {gameCount} {gameCount !== 1 ? t('dash.games') : t('dash.game')}</>}
      </div>

      {/* Group legend */}
      <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        {groups && Object.entries(groups).map(([name, info]) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: colorMap[name] || '#94A3B8' }} />
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', fontWeight: 600, color: '#1A1D20' }}>
              {name}
            </span>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', color: '#94A3B8' }}>
              {info.practiced}/{info.total}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

